/**
 * Business Logic Layer - Metrics Service
 * Contains business rules for metrics and monitoring
 */

import { MetricsRepository, RequestMetric, MetricsSummary, TokenUsageMetric } from '../repositories/MetricsRepository';

export class MetricsService {
  private repository: MetricsRepository;

  constructor() {
    this.repository = MetricsRepository.getInstance();
  }

  /**
   * Get requests over time with error tracking
   */
  public async getRequestsOverTime(timeRange: string = '24h'): Promise<RequestMetric[]> {
    const data = await this.repository.getRequestsOverTime(timeRange);
    
    // Calculate error rate if not provided
    return data.map(metric => ({
      ...metric,
      errorRate: metric.errorRate ?? (metric.total > 0 ? (metric.errors / metric.total) * 100 : 0)
    }));
  }

  /**
   * Get error rate metrics
   */
  public async getErrorRate(timeRange: string = '24h'): Promise<RequestMetric[]> {
    return await this.repository.getErrorRate(timeRange);
  }

  /**
   * Get summary metrics
   */
  public async getSummaryMetrics(timeRange: string = '24h'): Promise<MetricsSummary> {
    return await this.repository.getSummaryMetrics(timeRange);
  }

  /**
   * Get token usage metrics over time
   */
  public async getTokenUsage(timeRange: string = '24h'): Promise<TokenUsageMetric[]> {
    return await this.repository.getTokenUsage(timeRange);
  }

  /**
   * Format time for display
   */
  public formatTime(isoString: string, timeRange: string): string {
    const date = new Date(isoString);
    
    if (timeRange === '24h' || timeRange === '7d') {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else if (timeRange === '30d') {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    
    return date.toLocaleString('en-US');
  }

  /**
   * Calculate percentage change
   */
  public calculateChange(current: number, previous: number): number {
    if (previous === 0) return 0;
    return ((current - previous) / previous) * 100;
  }
}

// Export singleton instance
export const metricsService = new MetricsService();
